import { python } from "@codemirror/lang-python";
import { foldGutter, foldKeymap } from "@codemirror/language";
import { Prec } from "@codemirror/state";
import { keymap } from "@codemirror/view";
import Add from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Clear";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Refresh from "@mui/icons-material/Refresh";
import { default as Save, default as SaveIcon } from "@mui/icons-material/Save";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Table as MuiTable,
  Paper,
  Select,
  Stack,
  Switch,
  SxProps,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CasLoader } from "./brand/CasLoader";
import CodeMirror from "@uiw/react-codemirror";
import * as R from "ramda";
import React, { CSSProperties, useEffect, useState } from "react";
import {
  fetchTableCount,
  fetchTablePage,
  getTableLogs,
  setTableRunFrequency as setTableRunFrequencyApi,
} from "../api/table";
import { useAppId } from "../hook";
import {
  useAddTableInputsOptions,
  useFetchTableLogs,
  useFetchTableRunHistory,
  useFetchTableSpecs,
  useGetTableLogsForTable,
  useIsTableNotFound,
  useRunTable,
  useSaveTableColumns,
  useSaveTableInputs,
  useSetTableCode,
  useSetTableTrigger,
  useTable,
  useTableInputs,
  useTableRunHistory,
  useTableSearch,
  useTableSpec,
  useTableType,
} from "../hook/table";
import {
  TableColumn,
  TableRunFrequency,
  TableRunHistoryElement,
  TableSpec,
  TableType,
  TriggerIndex,
  indexToTrigger,
  tableOrNotFoundAsTable,
  tableRunAsError,
  tableRunAsSuccess,
  triggerToIndex,
} from "../types/table";
import { useIsDarkMode } from "../hook/setting";

export const DataTable: React.FC<{ tableId: string | undefined }> = ({
  tableId,
}) => {
  const table = useTable(tableId);
  const tableHeader = table?.header ?? [];
  const {
    fetchTable,
    isLoading,
    rows,
    tableCount,
    setPage,
    page,
    header: fetchedHeader,
  } = useTableSearch(tableId);
  const header = fetchedHeader ?? tableHeader;

  return (
    <Box sx={{ flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <IconButton color="primary" onClick={() => fetchTable()}>
          <Refresh />
        </IconButton>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: "40rem" }}>
        <MuiTable stickyHeader>
          <TableHead>
            <TableRow>
              {header?.map((name: any, index: number) => (
                <TableCell sx={{ height: "4rem" }} key={index}>
                  {name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {!isLoading && (
            <TableBody>
              {rows?.map((row: any, rowIndex: number) => (
                <TableRow key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => {
                    return (
                      <TableCell sx={{ height: "4rem" }} key={cellIndex}>
                        {JSON.stringify(cell)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          )}
        </MuiTable>
      </TableContainer>
      {isLoading && (
        <Box
          sx={{
            height: "40rem",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            padding: "2rem",
          }}
        >
          <CasLoader />
        </Box>
      )}
      <TablePagination
        component="div"
        onPageChange={(e, value) => setPage(value)}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        page={page}
        count={tableCount}
        showFirstButton={true}
        showLastButton={true}
      />
    </Box>
  );
};

export const CodeTabCode: React.FC<{ tableSpec?: TableSpec }> = ({
  tableSpec,
}) => {
  const [editing, setEditing] = useState(true);
  const [code, _setCode] = useState("");
  const setTableCode = useSetTableCode();
  const tableId = tableSpec?.["table-id"]!;
  const runTable = useRunTable();
  const specCode = tableSpec?.code;
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editControlsDisabled = !unsavedChanges || isSaving;
  const hideCircularProgessSxProps: SxProps = isSaving
    ? {}
    : { display: "none" };
  const theme = useTheme();
  const {isDarkMode} = useIsDarkMode();

  const setCode = (code: string) => {
    const cleanedCode = code.replace(/^```python\s*|\s*```$/g, "");
    _setCode(cleanedCode);
  };

  useEffect(() => {
    setCode(specCode ?? "");
    setUnsavedChanges(false);
  }, [specCode]);

  const onCancel = () => {
    setUnsavedChanges(false);
    setCode(specCode ?? "");
  };

  const onSave = () => {
    if (isSaving) return;

    setIsSaving(true);
    setTableCode({ code, tableId }).then(() => {
      setIsSaving(false);
      setUnsavedChanges(false);
    });
  };

  const onRun = () => {
    if (isSaving) return;

    setIsSaving(true);
    setTableCode({ code, tableId }).then(() => {
      setIsSaving(false);
      setUnsavedChanges(false);
      runTable({ tableId });
    });
  };

  const codeKeyMap = Prec.highest(
    keymap.of([
      {
        key: "Ctrl-s",
        preventDefault: true,
        run: () => {
          onSave();
          return true;
        },
      },
      {
        preventDefault: true,
        stopPropagation: true,
        key: "Ctrl-Enter",
        run: () => {
          onRun();
          return true;
        },
      },
      ...foldKeymap,
    ])
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Code</Typography>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <CasLoader
            sx={hideCircularProgessSxProps}
            size={24}
          />
          <IconButton
            color="primary"
            onClick={onSave}
            disabled={editControlsDisabled}
          >
            <SaveIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={onCancel}
            disabled={editControlsDisabled}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      </Box>
      <CodeMirror
        editable={true}
        value={code}
        theme={isDarkMode ? "dark" : "light"}
        height="40rem"
        extensions={[codeKeyMap, python(), foldGutter()]}
        onChange={(value: any) => {
          setUnsavedChanges(true);
          setCode(value);
        }}
      />
    </>
  );
};

export const CodeTabLogs: React.FC<{ tableSpec?: TableSpec }> = ({
  tableSpec,
}) => {
  const getTableLogsForTable = useGetTableLogsForTable();
  const logs = getTableLogsForTable(tableSpec?.["table-id"]);
  const [logsLoading, setLogsLoading] = useState(true);
  const tableId = tableSpec?.["table-id"];
  const fetchTableLogs = useFetchTableLogs();
  const theme = useTheme();
  const {isDarkMode} = useIsDarkMode();
  const colorProps: CSSProperties = isDarkMode
    ? { backgroundColor: "rgb(29, 31, 33)", color: "rgb(197, 200, 198)" }
    : { backgroundColor: theme.palette.grey.A200 };

  useEffect(() => {
    if (!tableId) return;
    setLogsLoading(true);
    fetchTableLogs(tableId).then(() => {
      setLogsLoading(false);
    });
  }, [tableId]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Logs</Typography>
      </Box>
      {logsLoading ? (
        <Box
          sx={{ padding: "1rem", display: "flex", justifyContent: "center" }}
        >
          <CasLoader />
        </Box>
      ) : (
        <div
          style={{
            width: "100%",
            height: "40rem",
            padding: "1rem",
            overflowY: "auto",
            whiteSpace: "pre",
            ...colorProps,
          }}
        >
          {logs ? logs : "No Logs"}
        </div>
      )}
    </>
  );
};

export const CodeTab: React.FC<{ tableSpec?: TableSpec }> = ({ tableSpec }) => {
  const paperStyles = {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    height: "50rem",
    width: "50%",
    padding: 1,
  };
  return (
    <Box
      sx={{
        flexDirection: "row",
        display: "flex",
      }}
    >
      <Paper elevation={7} sx={{ ...paperStyles, width: "66%" }}>
        <CodeTabCode tableSpec={tableSpec} />
      </Paper>
      <Paper elevation={7} sx={{ ...paperStyles, width: "33%" }}>
        <CodeTabLogs tableSpec={tableSpec} />
      </Paper>
    </Box>
  );
};

export const LogsTab: React.FC<{ tableSpec?: TableSpec }> = ({ tableSpec }) => {
  const [logs, setLogs] = useState<string | undefined>();
  const [logsLoading, setLogsLoading] = useState(true);
  const tableId = tableSpec?.["table-id"];
  const [appId, setAppId] = useAppId();

  useEffect(() => {
    if (!tableId) return;
    setLogsLoading(true);
    getTableLogs({ tableId, appId: appId! }).then(({ logs }) => {
      setLogsLoading(false);
      setLogs(logs);
    });
  }, [tableId]);

  return (
    <Box sx={{ overflowY: "auto" }}>
      {logsLoading ? (
        <Box
          sx={{ padding: "1rem", display: "flex", justifyContent: "center" }}
        >
          <CasLoader />
        </Box>
      ) : (
        <pre
          style={{
            backgroundColor: "rgb(29, 31, 33)",
            color: "rgb(197, 200, 198)",
          }}
        >
          {logs}
        </pre>
      )}
    </Box>
  );
};

export const UpdatesTab: React.FC<{ tableSpec?: TableSpec }> = ({
  tableSpec,
}) => {
  const tableId = tableSpec?.["table-id"];
  const trigger = tableSpec?.trigger!;
  const [appId, setAppId] = useAppId();
  const [isLoading, setIsLoading] = useState(false);
  const selectValue = Number(
    trigger ? triggerToIndex[trigger] : TriggerIndex.ALWAYS
  );
  const setTableTrigger = useSetTableTrigger();
  const [tableRunFrequency, setTableRunFrequency] = useState<TableRunFrequency>(
    {}
  );
  const fetchTableSpecs = useFetchTableSpecs();

  const seconds = tableRunFrequency?.seconds;
  const minutes = tableRunFrequency?.minutes;
  const hours = tableRunFrequency?.hours;
  const days = tableRunFrequency?.days;
  const setSeconds = (seconds: number) =>
    setTableRunFrequency({ ...tableRunFrequency, seconds });
  const setMinutes = (minutes: number) =>
    setTableRunFrequency({ ...tableRunFrequency, minutes });
  const setHours = (hours: number) =>
    setTableRunFrequency({ ...tableRunFrequency, hours });
  const setDays = (days: number) =>
    setTableRunFrequency({ ...tableRunFrequency, days });

  const onSave = () => {
    if (!appId) return;
    if (!tableId) return;

    setIsLoading(true);
    setTableRunFrequencyApi({
      appId: appId,
      tableId: tableId,
      runFrequency: tableRunFrequency,
    })
      .then(() => {
        fetchTableSpecs({ tableIds: [tableId] });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!tableSpec) return;

    setTableRunFrequency(tableSpec?.update_interval);
  }, [tableSpec]);

  return (
    <Box sx={{ overflowY: "auto" }}>
      <Box
        sx={{
          padding: "1rem",
          gap: 3,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          sx={{ padding: "1rem", flexGrow: 1, flexBasis: 1 }}
          elevation={5}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Run Frequency</Typography>
            <Typography variant="body1">
              This is how often this code is executed.
            </Typography>
            <TextField
              label="Days"
              type="number"
              onChange={(e) => setDays(Number(e.target.value))}
              value={days ?? 0}
              disabled={isLoading}
            />
            <TextField
              label="Hours"
              type="number"
              onChange={(e) => setHours(Number(e.target.value))}
              value={hours ?? 0}
              disabled={isLoading}
            />
            <TextField
              label="Minutes"
              type="number"
              onChange={(e) => setMinutes(Number(e.target.value))}
              value={minutes ?? 0}
              disabled={isLoading}
            />
            <TextField
              label="Seconds"
              type="number"
              onChange={(e) => setSeconds(Number(e.target.value))}
              value={seconds ?? 0}
              disabled={isLoading}
            />
            <Box
              sx={{
                justifyContent: "end",
                display: "flex",
                alignItems: "center",
              }}
            >
              {isLoading && (
                <CasLoader size={"1rem"} sx={{ color: "primary" }} />
              )}
              <Button disabled={isLoading} onClick={() => onSave()}>
                Save
              </Button>
            </Box>
          </Box>
        </Paper>
        <Paper
          sx={{ padding: "1rem", flexGrow: 1, flexBasis: 1 }}
          elevation={5}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Trigger</Typography>
            <Typography variant="body1">
              This is what constitutes for an input to this code.
            </Typography>
            <Typography variant="body1">
              Always: Every row in the input table will be included as an input
              to this code.
            </Typography>
            <Typography variant="body1">
              Edit: Only rows that change value from the last time this has run.
              This means another value other than the primary key.
            </Typography>
            <Typography variant="body1">
              Delete: Only rows that are no longer present since the last time
              this has run. This means any row in which the primary key is no
              longer present.
            </Typography>
            <Typography variant="body1">
              Create: Only new rows since the last time this has run. This means
              any row in which a distinct primary key has been added to the
              input since the last time this code was run.
            </Typography>
            <FormControl>
              <InputLabel id="update-trigger-label">Trigger</InputLabel>
              <Select
                label="Trigger"
                labelId="update-trigger-label"
                aria-label="Update Trigger"
                value={selectValue}
                onChange={(e) => {
                  const numVal = Number(e.target.value) as TriggerIndex;
                  const newTrigger = indexToTrigger[numVal];
                  setTableTrigger({ tableId: tableId!, trigger: newTrigger });
                }}
              >
                <MenuItem value={TriggerIndex.ALWAYS}>Always</MenuItem>
                <MenuItem value={TriggerIndex.CREATE}>Create</MenuItem>
                <MenuItem value={TriggerIndex.UPDATE}>Update</MenuItem>
                <MenuItem value={TriggerIndex.DELETE}>Delete</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export const InputsTab: React.FC<{ tableSpec?: TableSpec }> = ({
  tableSpec,
}) => {
  const tableId = tableSpec?.["table-id"];
  const tableInputs = useTableInputs(tableId);
  const [addInputsModalOpen, setAddInputsModalOpen] = useState(false);
  const saveTableInputs = useSaveTableInputs();
  const addOptions = useAddTableInputsOptions({ tableId });

  return (
    <Box sx={{ overflowY: "auto", flexDirection: "column" }}>
      <Modal open={addInputsModalOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Paper
            elevation={7}
            sx={{
              padding: "2rem",
              width: "80rem",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Table Id</TableCell>
                  <TableCell>File Id</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Add</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addOptions.map((table, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        {table?.["table-id"] ?? "Not Found!"}
                      </TableCell>
                      <TableCell>
                        {table?.["file-id"] ?? "Not Found!"}
                      </TableCell>
                      <TableCell>{table?.name ?? "Not Found!"}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => {
                            if (!tableId) return;

                            const newInputs = [
                              ...tableInputs.map((table) => table["file-id"]!),
                              table["file-id"]!,
                            ];
                            const config = { tableId, inputs: newInputs };
                            saveTableInputs(config);
                            setAddInputsModalOpen(false);
                          }}
                        >
                          Add
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <Button
                onClick={() => {
                  setAddInputsModalOpen(false);
                }}
              >
                Close
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box></Box>
        <Box>
          <IconButton
            onClick={() => {
              setAddInputsModalOpen(true);
            }}
            color="primary"
          >
            <Add />
          </IconButton>
        </Box>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Table ID</TableCell>
            <TableCell>File ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Remove</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableInputs.map((table, index) => {
            const asTable = tableOrNotFoundAsTable(table);

            return (
              <TableRow key={index}>
                <TableCell>
                  <Button
                    onClick={() => {
                      const tableId = table["table-id"];
                      if (!tableId) return;
                      // navigateTable(tableId);
                    }}
                  >
                    {tableId}
                  </Button>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      navigator.clipboard.writeText(table["table-id"]!);
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </TableCell>
                <TableCell>
                  {table["file-id"]}
                  <IconButton
                    color="primary"
                    onClick={() => {
                      navigator.clipboard.writeText(table["file-id"]!);
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </TableCell>
                <TableCell>{asTable?.name ?? "Not Found!"}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      // console.log("DELETING", tableId);
                      if (!tableId) return;
                      const newInputs = tableInputs
                        .map((table) => table["file-id"]!)
                        .filter((fileId) => table["file-id"]! !== fileId);
                      const config = { tableId, inputs: newInputs };
                      // console.log("NEW_INPUTS", newInputs);
                      saveTableInputs(config);
                      setAddInputsModalOpen(false);
                    }}
                    color="primary"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export const ColumnsTab: React.FC<{ tableSpec?: TableSpec }> = ({
  tableSpec,
}) => {
  const tableId = tableSpec?.["table-id"];
  const tableInputs = useTableInputs(tableId);
  const [appId, setAppId] = useAppId();
  const columns = tableSpec?.output.columns;
  const [columnsState, setColumnsState] = useState(columns);
  const [addColumnModalOpen, setAddColumnModalOpen] = useState(false);
  const [addColumnName, setAddColumnName] = useState("");
  const numPrimaryKeys =
    columnsState?.map((col) => (col.is_primary_key ? 1 : 0)).reduce(R.add, 0) ??
    0;
  const saveTableColumns = useSaveTableColumns();

  const onSave = () => {
    if (!tableId) return;
    if (!columnsState) return;
    saveTableColumns({ tableId, columns: columnsState });
  };

  const onCancel = () => {
    setColumnsState(columns);
  };

  const setIsPrimaryKeyColumn: (
    column: TableColumn,
    isPrimaryKey: boolean
  ) => TableColumn = (column, isPrimaryKey) =>
    isPrimaryKey
      ? R.assoc("is_primary_key", isPrimaryKey, column)
      : R.dissoc("is_primary_key", column);

  const addColumn = (name: string) => {
    if (!columnsState) return;
    setColumnsState([...columnsState, { name }]);
  };

  const setIsPrimaryKey = (index: number, isPrimaryKey: boolean) => {
    if (!columnsState) return;
    const disablingLastPrimaryKey = numPrimaryKeys <= 1 && !isPrimaryKey;
    if (disablingLastPrimaryKey) return;

    const newColumnsState = R.adjust(
      index,
      (col) => setIsPrimaryKeyColumn(col, isPrimaryKey),
      columnsState
    );
    setColumnsState(newColumnsState);
  };

  const setColumnName = (index: number, name: string) => {
    if (!columnsState) return;
    const newColumnsState = R.adjust(
      index,
      R.assoc("name", name),
      columnsState
    );
    setColumnsState(newColumnsState);
  };

  useEffect(() => {
    setColumnsState(columns);
  }, [columns]);

  return (
    <Box>
      <Modal open={addColumnModalOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Paper
            elevation={7}
            sx={{
              padding: "2rem",
              width: "40rem",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6">Add Column</Typography>
            <TextField
              label="Name"
              value={addColumnName}
              onChange={(e) => setAddColumnName(e.target.value)}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <Button
                onClick={() => {
                  addColumn(addColumnName);
                  setAddColumnName("");
                  setAddColumnModalOpen(false);
                }}
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  setAddColumnModalOpen(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography>Must have at least 1 primary key.</Typography>
        <Box>
          <IconButton
            onClick={() => {
              onCancel();
            }}
          >
            <CancelIcon color="primary" />
          </IconButton>
          <IconButton
            onClick={() => {
              onSave();
            }}
          >
            <Save color="primary" />
          </IconButton>
          <IconButton
            onClick={() => {
              setAddColumnModalOpen(true);
            }}
          >
            <Add color="primary" />
          </IconButton>
        </Box>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Primary Key</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {columnsState?.map((column, i) => {
            return (
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    onChange={(e) => setColumnName(i, e.target.value)}
                    value={column.name}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    onChange={(e) => {
                      const isPrimaryKey = e.target.checked;
                      setIsPrimaryKey(i, isPrimaryKey);
                    }}
                    checked={column.is_primary_key ? true : false}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export const RunHistoryTab: React.FC<{ tableSpec?: TableSpec }> = ({
  tableSpec,
}) => {
  const tableId = tableSpec?.["table-id"];
  const header = [
    "run-id",
    "timestamp",
    "inserts",
    "updates",
    "deletes",
    "Error",
    "Error Line",
    "Error Message",
  ];
  const tableRunHistory = useTableRunHistory(tableId);
  const fetchTableRunHistory = useFetchTableRunHistory();
  const runs = tableRunHistory?.["table-runs"];
  const isLoading = tableRunHistory === undefined;

  useEffect(() => {
    if (!tableId) return;
    if (tableRunHistory) return;
    fetchTableRunHistory(tableId);
  }, [tableId, tableRunHistory]);

  return (
    <Box sx={{ flexDirection: "column" }}>
      <TableContainer component={Paper} sx={{ maxHeight: "40rem" }}>
        <MuiTable stickyHeader>
          <TableHead>
            <TableRow>
              {header?.map((name: any, index: number) => (
                <TableCell sx={{ height: "4rem" }} key={index}>
                  {name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {!isLoading && (
            <TableBody>
              {runs?.map(
                (runElement: TableRunHistoryElement, rowIndex: number) => {
                  const run = runElement["table-run"];
                  const time = new Date(runElement.timestamp);
                  const timeFormat = time.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  });
                  const runId = runElement["run-id"];
                  const runSuccess = tableRunAsSuccess(run);
                  const runError = tableRunAsError(run);
                  const runStats = runSuccess?.["write-table-rows"]?.stats;
                  const inserts = runStats?.insert ?? 0;
                  const updates = runStats?.update ?? 0;
                  const deletes = runStats?.delete ?? 0;
                  const errorLine = runError?.error_line;
                  const errorMessage = runError?.message;
                  const error = runSuccess === undefined ? "true" : "false";

                  return (
                    <TableRow key={rowIndex}>
                      <TableCell>{runId}</TableCell>
                      <TableCell>{timeFormat}</TableCell>
                      <TableCell>{inserts}</TableCell>
                      <TableCell>{updates}</TableCell>
                      <TableCell>{deletes}</TableCell>
                      <TableCell>{error}</TableCell>
                      <TableCell>{errorLine}</TableCell>
                      <TableCell>{errorMessage}</TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          )}
        </MuiTable>
      </TableContainer>
      {isLoading && (
        <Box
          sx={{
            height: "40rem",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            padding: "2rem",
          }}
        >
          <CasLoader />
        </Box>
      )}
    </Box>
  );
};

export const ErrorTab: React.FC<{
  tableSpec: TableSpec;
}> = ({ tableSpec }) => {
  const { error, error_line, error_line_number, message, stack_trace } =
    tableSpec["execute-error"]!;

  return (
    <Box padding={2}>
      <Typography variant="h5" color="error" gutterBottom>
        Error: {error || "Unknown Error"}
      </Typography>
      {message && (
        <Typography variant="body1" gutterBottom>
          <strong>Message:</strong> {message}
        </Typography>
      )}
      {error_line && (
        <Typography variant="body1" gutterBottom>
          <strong>Error Line:</strong> {error_line} (Line {error_line_number})
        </Typography>
      )}
      {stack_trace && (
        <Paper
          elevation={7}
          style={{ padding: "1rem", marginTop: "1rem", overflowX: "auto" }}
        >
          <Typography variant="subtitle1" gutterBottom>
            <strong>Stack Trace:</strong>
          </Typography>
          <Typography variant="body2" component="pre">
            {stack_trace}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export const CodeTableFound: React.FC<{
  tableId: string | undefined;
}> = ({ tableId }) => {
  const tableSpec = useTableSpec(tableId);
  const [selectedTab, setSelectedTab] = useState(0);
  const sideEffectTable = tableSpec?.["code-type"] === "side-effect";
  const isError = tableSpec?.["execute-error"] ? true : false;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Typography>Table ID: {tableId}</Typography>
        <IconButton
          color="primary"
          onClick={() => {
            navigator.clipboard.writeText(tableId ?? "");
          }}
        >
          <ContentCopy />
        </IconButton>
      </Box>
      {tableSpec && (
        <>
          <Tabs value={selectedTab}>
            {isError ? (
              <Tab
                color="primary"
                label="Error"
                onClick={() => setSelectedTab(0)}
              />
            ) : (
              <Tab
                color="primary"
                label="Table"
                onClick={() => setSelectedTab(0)}
              />
            )}
            <Tab
              color="primary"
              label="Code"
              onClick={() => setSelectedTab(1)}
            />
            <Tab
              color="primary"
              label="Inputs"
              onClick={() => setSelectedTab(2)}
            />
            <Tab
              color="primary"
              label="Run History"
              onClick={() => setSelectedTab(3)}
            />
            <Tab
              color="primary"
              label="Columns"
              onClick={() => setSelectedTab(4)}
            />
            {sideEffectTable && (
              <Tab
                color="primary"
                label="Update Trigger"
                onClick={() => setSelectedTab(5)}
              />
            )}
          </Tabs>
          {selectedTab === 0 ? (
            isError ? (
              <ErrorTab tableSpec={tableSpec!} />
            ) : (
              <DataTable tableId={tableId} />
            )
          ) : selectedTab === 1 ? (
            <CodeTab tableSpec={tableSpec} />
          ) : selectedTab === 2 ? (
            <InputsTab tableSpec={tableSpec} />
          ) : selectedTab === 3 ? (
            <RunHistoryTab tableSpec={tableSpec} />
          ) : selectedTab === 4 ? (
            <ColumnsTab tableSpec={tableSpec} />
          ) : selectedTab === 5 ? (
            <UpdatesTab tableSpec={tableSpec} />
          ) : (
            <Typography>Error: Tab not found</Typography>
          )}
        </>
      )}
    </>
  );
};

export const CodeTableNotFound: React.FC<{}> = () => {
  return (
    <Paper elevation={9} sx={{ padding: 2 }}>
      <Typography color={"error"}>Table Not Found</Typography>
    </Paper>
  );
};

export const CodeTable: React.FC<{
  tableId: string | undefined;
}> = ({ tableId }) => {
  const isTableNotFound = useIsTableNotFound();
  const tableNotFound = isTableNotFound(tableId);

  return tableNotFound ? (
    <CodeTableNotFound />
  ) : (
    <CodeTableFound tableId={tableId} />
  );
};

export const SearchTable = React.memo<{
  tableId: string | undefined;
}>(({ tableId }) => {
  const tableType = useTableType(tableId);

  return (
    <Stack spacing={2}>
      {tableType === TableType.CODE_TABLE && <CodeTable tableId={tableId} />}
      {tableType === TableType.DATA_TABLE && <DataTable tableId={tableId} />}
    </Stack>
  );
});
